/**
 * One row of `Plany klientów`: a client, the plan they are actually running, and how far through it
 * they are.
 *
 * The scope's whole read is `GET /plan-assignment` — the SAME request the inspector's follower strip
 * subscribes to (`api/inspectorQueries`), with a different projection over it. Owner decision D11,
 * 3 September 2026: the backend does not move, the assignment list already carries every fact this
 * list draws, and one observer serves both.
 *
 * ── This is the only module in the slice that names `PlanAssignmentSummary` ──────────────────
 * `planLibraryRow` owns the library item, `inspectorView` owns the full plan, and this owns the
 * assignment. Three wire shapes, three projections, and nothing above `model/` sees any of them.
 *
 * ── Active and Paused, and nothing else ──────────────────────────────────────────────────────
 * `assignmentStatus` owns that rule and the inspector's follower strip reads the same one, so the
 * scope's list and the count beside it cannot be built from two different populations.
 *
 * A paused plan keeps its row, its bar and its percent, and says `Wstrzymany` where a live one says
 * how many workouts are done (owner decision D7).
 *
 * ── The FORK is what a row opens, and the SOURCE is only where it came from ──────────────────
 * `planTemplateId` is the client's own copy of the plan — their days, their progression, their
 * edits. `sourceTemplateId` is the library template it was forked from, which is what the templates
 * scope groups by and what a plan built for one client directly does not have at all. A row links to
 * the FORK: opening the source would show the trainer a different document under the client's name.
 *
 * ── The progress figure is the mobile app's, to the digit ────────────────────────────────────
 * `move-with-me/src/features/plans/model/mapAssignment.ts` computes `progressPct` as
 * `total > 0 ? round(completed / total * 100) : 0`, and `PlanAssignmentRow` prints
 * `{completed} / {total} treningów` beside it. Two clients disagreeing about one number across two
 * apps is the failure this avoids, so the rule is ported rather than re-derived. A zero total is a
 * real answer — a plan whose workouts have not been generated yet — and gives 0%, not a division.
 *
 * No React, no DOM, no query hook, no i18next. The collator's locale arrives as a parameter for the
 * reason `templateFollowers`'s does: a rule that reads the ambient language cannot be tested against
 * a second one.
 */

import { planCategoryKey, planCoverPair, type PlanCategoryKey } from '~/entities/plan-draft';
import type { TrainWithMeModulesTrainingDtosResponsesPlanAssignmentSummary as PlanAssignmentSummary } from '~/shared/api/generated/model';

import { liveAssignmentStatus, type ClientPlanStatus } from '~/entities/plan-draft';
import {
  categoryLabel,
  schedulingMarkerOf,
  textOrNull,
  type PlanSchedulingMarker,
  type TranslateKey,
} from './planLibraryRow';
import { searchTextOf } from './searchFold';
import { personView, surnameOf, type PersonView } from './templateFollowers';

export type { ClientPlanStatus };

export interface ClientPlanRow {
  /** The assignment's own id. The row's React key, because one client can hold two plans. */
  readonly assignmentId: number;
  /** The client's FORK — what `Edytuj` and a click on the row open, and what the inspector reads. */
  readonly planTemplateId: number;
  /** The library template it came from, or `null` for a plan built for this client directly. */
  readonly sourceTemplateId: number | null;
  readonly customerId: number;
  /** As the backend composed it. Never composed again here — see `templateFollowers`. */
  readonly client: PersonView;
  /** The plan's name, which is the row's SUB-line: the client's name is the title. */
  readonly planName: string;
  readonly categoryKey: PlanCategoryKey | null;
  /**
   * The trainer's word for the category — "Mobilność", never the wire's `"Flexibility"`. The
   * inspector's chip prints it, and it is on the ROW so the chip does not wait for `/full`.
   * `planLibraryRow`'s rule exactly: `null` where the plan carries none, and the server's own word
   * where it carries one this build has no label for.
   */
  readonly category: string | null;
  /**
   * The client's name and the plan's, folded once, for the search box to match against.
   *
   * `planLibraryRow`'s rule and `searchFold`'s fold — see that module for why the fold happens on
   * the response rather than on every keystroke.
   */
  readonly searchText: string;
  /** The row's 51×40 crop and the inspector's plate. Never null; the category's pool is the fallback. */
  readonly thumbUrl: string;
  readonly heroUrl: string;
  /** `null` for a weekly plan and for a mode this build has no word for. See `planLibraryRow`. */
  readonly schedulingMarker: PlanSchedulingMarker | null;
  readonly status: ClientPlanStatus;
  readonly workoutsDone: number;
  readonly workoutsTotal: number;
  /** 0–100, rounded, and 0 when the plan has no workouts yet. Mobile's own rule. */
  readonly progressPct: number;
}

/** Mobile's `progressPct`, ported. Reached through `toClientPlanRows`, which is what a test pins. */
function progressPct(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((completed / total) * 100);
}

/**
 * Project one assignment, or `null` where it is not a row this scope draws.
 *
 * Four ways an assignment is refused, and each is a real shape rather than defensive noise: a status
 * this scope does not list, and the three ids the row cannot be built without. A row keyed on zero
 * would collide with every other such row in React's reconciler and would link to `/plans/0`, which
 * 404s — the same guard `toPlanLibraryRows` applies to the library.
 */
function toClientPlanRow(
  assignment: PlanAssignmentSummary,
  translate: TranslateKey,
): ClientPlanRow | null {
  const status = liveAssignmentStatus(assignment.status);
  if (status === null) return null;

  const assignmentId = assignment.id ?? 0;
  const planTemplateId = assignment.planTemplateId ?? 0;
  const customerId = assignment.customerId ?? 0;
  if (assignmentId <= 0 || planTemplateId <= 0 || customerId <= 0) return null;

  const categoryKey = planCategoryKey(assignment.category);
  // The name is the cover's SEED as well as the row's sub-line, so it is read once. A nameless plan
  // keeps the empty string rather than acquiring placeholder copy the model has no business writing.
  const planName = textOrNull(assignment.templateName) ?? '';
  const workoutsTotal = assignment.workoutCount ?? 0;
  const workoutsDone = assignment.completedWorkoutCount ?? 0;

  const client = personView(customerId, assignment.client);

  return {
    assignmentId,
    planTemplateId,
    sourceTemplateId: assignment.sourceTemplateId ?? null,
    customerId,
    client,
    planName,
    categoryKey,
    category: categoryLabel(assignment.category, categoryKey, translate),
    // The scope's own placeholder promises `Szukaj klienta lub planu`, and the row is TITLED by the
    // client — a search that only matched the sub-line would miss the thing the trainer is reading.
    searchText: searchTextOf(client.fullName, planName),
    ...planCoverPair(assignment.coverImageUrl, categoryKey, planName),
    schedulingMarker: schedulingMarkerOf(assignment.schedulingMode),
    status,
    workoutsDone,
    workoutsTotal,
    progressPct: progressPct(workoutsDone, workoutsTotal),
  };
}

/**
 * The scope's read entry point: the trainer's whole assignment list in, the rows out, in order.
 *
 * **Sorted by the CLIENT, then by the plan.** Owner decision, 3 September 2026 — this list answers
 * "who is running what", so it is alphabetical by the person, and a client holding two plans has
 * them together under their own name. `Intl.Collator` rather than `<`, because Polish orders `ą`
 * after `a` and `ł` after `l` and a code-point comparison puts both after `z`.
 *
 * `translate` resolves the category's LABEL, which the inspector's chip prints and must not wait
 * for `/full` to learn. Key-only, like every projection here.
 */
export function toClientPlanRows(
  assignments: readonly PlanAssignmentSummary[] | undefined,
  translate: TranslateKey,
  locale: string,
): ClientPlanRow[] {
  if (assignments === undefined) return [];
  const collator = new Intl.Collator(locale, { sensitivity: 'base' });
  return assignments
    .map((assignment) => toClientPlanRow(assignment, translate))
    .filter((row): row is ClientPlanRow => row !== null)
    .sort(
      (a, b) =>
        collator.compare(surnameOf(a.client.fullName), surnameOf(b.client.fullName)) ||
        collator.compare(a.client.fullName, b.client.fullName) ||
        collator.compare(a.planName, b.planName),
    );
}
